/**
 * Tracks when the user last genuinely interacted with this tab, so token requests can
 * tell Identity how long the user has been idle. Background polling and timers never
 * count: only trusted input events, the tab becoming visible, and the window gaining focus.
 */

const INPUT_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
// Capture so scrolling inside any element counts, not just the page itself.
const INPUT_LISTENER_OPTIONS = { capture: true, passive: true };
const RECORD_THROTTLE_MS = 5 * 1000;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

export class UserActivityTracker {
  constructor({ isUserInteraction = (event) => event.isTrusted } = {}) {
    this.isUserInteraction = isUserInteraction;
    // Loading or reloading the page is itself an interaction.
    this.lastInteractionAt = Date.now();
    this.onInteraction = null;
  }

  idleMs() {
    return Math.max(0, Math.floor(Date.now() - this.lastInteractionAt));
  }

  start(onInteraction) {
    if (this.onInteraction || !isBrowser()) return;

    this.onInteraction = onInteraction;
    INPUT_EVENTS.forEach((type) => window.addEventListener(type, this.handleInput, INPUT_LISTENER_OPTIONS));
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    // Not captured: element focus does not bubble, so only the window itself gaining focus arrives here.
    window.addEventListener('focus', this.handleInput);
  }

  stop() {
    if (!this.onInteraction || !isBrowser()) return;

    INPUT_EVENTS.forEach((type) => window.removeEventListener(type, this.handleInput, INPUT_LISTENER_OPTIONS));
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleInput);
    this.onInteraction = null;
  }

  handleInput = (event) => {
    if (this.isUserInteraction(event)) this.recordInteraction();
  };

  handleVisibilityChange = (event) => {
    if (document.visibilityState === 'visible') this.handleInput(event);
  };

  recordInteraction() {
    const now = Date.now();
    if (now - this.lastInteractionAt < RECORD_THROTTLE_MS) return;

    this.lastInteractionAt = now;
    this.onInteraction?.();
  }
}
